import { Subject } from "rxjs";
import { Synonym } from "~/classes/models/thesaurus/synonym.model";
import {
  CompleteWord,
  Word,
} from "~/classes/models/thesaurus/thesaurus-word.model";
import { WordList } from "~/classes/models/thesaurus/word-list.model";

export enum ThesaurusPageType {
  home,
  bannedWords,
  word,
  wordList,
}

export enum ThesaurusAction {
  openThesaurus,
  replaceWord,
}

export type ThesaurusPage =
  | {
      type: ThesaurusPageType.home;
      label: string;
      key: string;
      data?: undefined;
      isLoading: Ref<boolean>;
    }
  | {
      label: string;
      type: ThesaurusPageType.bannedWords;
      key: string;
      data: WordList;
      isLoading: Ref<boolean>;
    }
  | {
      label: string;
      type: ThesaurusPageType.word;
      key: string;
      data?: CompleteWord;
      isLoading: Ref<boolean>;
    }
  | {
      type: ThesaurusPageType.wordList;
      key: string;
      data: WordList;
      label: string;
    };

export const useDocumentThesaurus = (documentId: string) =>
  defineStore(`/document/${documentId}/thesaurus`, () => {
    const isInitialized = ref(false);
    const allWordLists = ref<WordList[]>([]);
    const wordLists = ref<WordList[]>([]);

    const approvedWordLists = ref<string[]>([
      "-ly Adverbs",
      "Quality Adjectives",
      "Strong Verbs",
      "Transitions",
    ]);

    const documentStore = useSubmittedDocumentStore(documentId);
    const { revision } = storeToRefs(documentStore);

    const initialize = async () => {
      try {
        const response = await $fetch("/api/thesaurus/word-lists", {
          headers: await useApiHeaders(),
        });
        allWordLists.value = response.map((data: any) => {
          return WordList.fromMap(data);
        });
        wordLists.value = allWordLists.value.filter((wordList: WordList) => {
          return approvedWordLists.value.includes(wordList.label);
        });
      } catch (e) {
        // Empty
      } finally {
        isInitialized.value = true;
      }
    };

    const pages = ref<ThesaurusPage[]>([
      {
        label: "Word Lists",
        type: ThesaurusPageType.home,
        key: "Word Lists",
        isLoading: ref(false),
      },
    ]);

    const loadWord = async (word: string) => {
      const response = await $fetch(`/api/thesaurus/words/${word}`, {
        headers: await useApiHeaders(),
      });

      return {
        word: Word.fromMap(response.word),
        synonyms: response.synonyms.map((synonym: any) =>
          Synonym.fromMap(synonym)
        ),
      };
    };

    const addPage = async (page: ThesaurusPage) => {
      switch (page.type) {
        case ThesaurusPageType.home:
          break;
        case ThesaurusPageType.bannedWords:
          break;
        case ThesaurusPageType.word:
          page.label = page.key;
          page.isLoading.value = true;
          loadWord(page.key).then((completeWord: CompleteWord) => {
            page.data = completeWord;
            page.isLoading.value = false;
          });
          break;
      }

      pages.value.push(page as any);
    };

    const currentPage = computed(() => {
      return pages.value[pages.value.length - 1];
    });

    const underlinedWords = computed(() => {
      if (revision.value?.studentSubmissionOps == undefined) return [];

      if (revision.value.studentSubmissionOps.length == 0) return [];

      if (revision.value.studentSubmissionOps.forEach == undefined) return [];

      const words: { text: string; startIndex: number; attributes: any }[] = [];
      let currentIndex = 0;

      revision.value.studentSubmissionOps.forEach((op: any) => {
        const length = typeof op.insert === "string" ? op.insert.length : 1;

        if (op.attributes?.underline == true) {
          words.push({
            text: typeof op.insert === "string" ? op.insert : "",
            startIndex: currentIndex,
            attributes: op.attributes,
          });
        }

        currentIndex += length;
      });

      return words;
    });

    const onPopToPage = (index: number) => {
      pages.value.splice(index + 1);
    };

    const documentText = ref<
      | {
          startIndex: number;
          endIndex: number;
          text: string;
        }
      | undefined
    >(undefined);

    const lookupWordFromRoot = (
      word: string,
      options?: {
        documentText?: {
          startIndex: number;
          endIndex: number;
          text: string;
        };
      }
    ) => {
      onPopToPage(0);
      addPage({
        type: ThesaurusPageType.word,
        key: word,
        data: undefined,
        isLoading: ref(true),
        label: word,
      });

      if (options?.documentText) {
        documentText.value = options.documentText;
      }
    };

    const thesaurusEventEmitter = new Subject<{
      event: ThesaurusAction;
      data?: any;
    }>();

    const internalWordLists = ref<{ [key: string]: WordList }>({
      wwwAsia: new WordList({
        id: "wwwAsia",
        label: "wwwAsia",
        words: ["when", "while", "where", "as", "since", "if", "although"],
        bannedWords: {},
        highlightStates: {},
      }),
      wwwAsiaB: new WordList({
        id: "wwwAsiaBanned",
        label: "wwwAsiaBanned",
        words: [
          "when",
          "while",
          "where",
          "as",
          "since",
          "if",
          "although",
          "because",
          "after",
          "before",
          "until",
          "unless",
          "whenever",
          "than",
        ],
        bannedWords: {},
        highlightStates: {},
      }),
    });

    const wordListById = computed(() => {
      return (id: string) => {
        if (internalWordLists.value[id] != undefined) {
          return internalWordLists.value[id];
        }

        const list = allWordLists.value.find((wordList) => wordList.id == id);

        if (list == undefined) {
          return undefined;
        }

        list.words.sort((a, b) => a.localeCompare(b));

        return list;
      };
    });

    const navigateToWordListById = (id: string) => {

      onPopToPage(0);
      const page = {
        type: ThesaurusPageType.wordList,
        key: wordList.id,
        data: wordList,
        label: wordList.label,
      } as ThesaurusPage;
      addPage(page);

      thesaurusEventEmitter.next({
        event: ThesaurusAction.openThesaurus,
      });
    };

    return {
      isInitialized,
      pages,
      underlinedWords,
      wordLists,
      initialize,
      currentPage,
      addPage,
      onPopToPage,
      lookupWordFromRoot,
      thesaurusEventEmitter,
      documentText,
      wordListById,
      navigateToWordListById,
    };
  });
